/*
 * Copyright (c) 2025 Bytedance, Inc. and its affiliates.
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  Tool,
  ToolCallEngine,
  StreamProcessingState,
  StreamChunkResult,
  ParsedModelResponse,
  MultimodalToolCallResult,
  ChatCompletionMessageParam,
  ChatCompletionChunk,
  AgentSingleLoopReponse,
} from '@multimodal/agent-interface';
import { buildToolCallResultMessages } from './utils';

/**
 * Abstract base class for tool call engines that provides common functionality
 * and enforces consistent behavior across different engine implementations.
 */
export abstract class BaseToolCallEngine implements ToolCallEngine {
  protected readonly useNativeToolCalls: boolean;

  constructor(useNativeToolCalls = false) {
    this.useNativeToolCalls = useNativeToolCalls;
  }

  /**
   * Abstract method to prepare the system prompt with tool information
   */
  abstract preparePrompt(instructions: string, tools: Tool[]): string;

  /**
   * Abstract method to prepare the request parameters
   */
  abstract prepareRequest(context: any): any;

  /**
   * Initialize stream processing state with common defaults
   */
  initStreamProcessingState(): StreamProcessingState {
    return {
      contentBuffer: '',
      toolCalls: [],
      reasoningBuffer: '',
      finishReason: null,
    };
  }

  /**
   * Abstract method to process streaming chunks
   */
  abstract processStreamingChunk(
    chunk: ChatCompletionChunk,
    state: StreamProcessingState,
  ): StreamChunkResult;

  /**
   * Finalize stream processing with common logic
   */
  finalizeStreamProcessing(state: StreamProcessingState): ParsedModelResponse {
    const finishReason = state.toolCalls.length > 0 ? 'tool_calls' : state.finishReason || 'stop';

    return {
      content: state.contentBuffer,
      reasoningContent: state.reasoningBuffer || undefined,
      toolCalls: state.toolCalls.length > 0 ? state.toolCalls : undefined,
      finishReason,
    };
  }

  /**
   * Build historical assistant message with common logic
   */
  buildHistoricalAssistantMessage(response: AgentSingleLoopReponse): ChatCompletionMessageParam {
    const message: ChatCompletionMessageParam = {
      role: 'assistant',
      content: response.content || '',
    };

    // Only add tool_calls for native engines
    if (this.useNativeToolCalls && response.toolCalls?.length) {
      message.tool_calls = response.toolCalls;
    }

    return message;
  }

  /**
   * Build historical tool call result messages using shared utility
   */
  buildHistoricalToolCallResultMessages(
    toolCallResults: MultimodalToolCallResult[],
  ): ChatCompletionMessageParam[] {
    return buildToolCallResultMessages(toolCallResults, this.useNativeToolCalls);
  }

  /**
   * Common helper to process reasoning content in chunks
   */
  protected processReasoningContent(
    chunk: ChatCompletionChunk,
    state: StreamProcessingState,
  ): string {
    const delta = chunk.choices[0]?.delta;
    let reasoningContent = '';

    // @ts-expect-error Not in OpenAI types but present in compatible LLMs
    if (delta?.reasoning_content) {
      // @ts-expect-error
      reasoningContent = delta.reasoning_content;
      state.reasoningBuffer += reasoningContent;
    }

    return reasoningContent;
  }

  /**
   * Common helper to process finish reason in chunks
   */
  protected processFinishReason(chunk: ChatCompletionChunk, state: StreamProcessingState): void {
    if (chunk.choices[0]?.finish_reason) {
      state.finishReason = chunk.choices[0].finish_reason;
    }
  }
}
